/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { BomItemController } from 'controllers/Project/BomItemController'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { uniqBy } from 'lodash'
import {
  IssueCode,
  RowDto,
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { UIActions } from 'store/UI/UIActions'
import { newProjectActions } from '../newProjectReducer'
import { bomItemSelector } from '../selectors/bomItemSelector'
import { projectSelectors } from '../selectors/projectSelectors'
import i18n from 'i18next'
import { projectAPIBuilder } from './BoMAsyncActions'
import { fetchProject } from './fetchProject'

export const SolveIssueAsync = createAsyncThunk<any, any, { state: any }>(
  'bom/solveIssue',
  async (
    {
      bomItemId,
      issueCode,
    }: {
      bomItemId: string
      issueCode: IssueCode
    },
    thunkAPI
  ) => {
    try {
      const controller = new BomItemController()

      const bomItem = bomItemSelector(thunkAPI.getState(), bomItemId) as RowDto

      thunkAPI.dispatch(
        newProjectActions.setBomItemProperties({
          id: bomItemId,
          issues: bomItem.issues?.filter((x) => x.issueCode !== issueCode),
          replacementBehaviour: 'keepLatestSourceIfArrayOrToken',
        })
      )

      return await controller.SolveIssue([bomItemId], issueCode)
    } catch (ex) {
      ShowException('solve issue', ex)
      throw ex
    }
  }
)

export const SolveAllIssuesByIssueCodeAsync = createAsyncThunk<
  any,
  any,
  { state: any }
>(
  'bom/solveAllIssuesByIssueCode',
  async (
    {
      issueCode,
    }: {
      issueCode: IssueCode
    },
    thunkAPI
  ) => {
    try {
      const currentState = thunkAPI.getState()
      const notAllowedWorkingSteps: WorkingStepDto[] = []
      const bomItemsIds = new Set<string>()

      currentState.project.partTypeIds.forEach((partTypeId) => {
        const bomItem = projectSelectors.bomItemSelector(
          currentState,
          partTypeId
        ) as RowDto

        const workingStepNotAvailableIssue = bomItem.issues?.find(
          (x) => x.issueCode === IssueCode.WorkingStepNotAvailable
        )

        if (workingStepNotAvailableIssue) {
          const notAvailableWorkingStepsTypeInThisRow: WorkingStepType[] =
            JSON.parse(
              workingStepNotAvailableIssue.metadata
            ).NotAllowedWorkingSteps

          if (notAvailableWorkingStepsTypeInThisRow?.length > 0) {
            notAvailableWorkingStepsTypeInThisRow.forEach((wsType) => {
              notAllowedWorkingSteps.push(
                bomItem.workingSteps.find(
                  (x) => x.primaryWorkingStep === wsType
                )
              )
            })
            bomItemsIds.add(partTypeId)
          }
        }
      })

      const modalDescription = i18n.t('project:resolve-issues-parts-count', {
        'part-count': bomItemsIds.size,
        workingSteps: uniqBy(
          notAllowedWorkingSteps,
          (x) => x.primaryWorkingStep
        )
          .map((workingStep) =>
            i18n.t(`common:${workingStep.primaryWorkingStep}`)
          )
          .join(' / '),
        defaultValue:
          'allow working steps {{workingSteps}} over {{part-count}} parts?',
      })

      thunkAPI.dispatch(
        UIActions.OpenModal('ConfirmationDialog', {
          title: i18n.t('project:resolve-issues-title', 'solve issues'),
          message: modalDescription,
          onConfirm: async () => {
            try {
              const controller = new BomItemController()

              return await controller.SolveIssue(
                Array.from(bomItemsIds),
                issueCode
              )
            } catch (err) {
              ShowException(modalDescription, err)
              thunkAPI.dispatch(
                fetchProject({
                  projectId: currentState.project.activeProject?.id,
                })
              )
              throw err
            }
          },
        })
      )
    } catch (ex) {
      ShowException('solve all issues by issue code', ex)
      throw ex
    }
  }
)

export const RemoveRowsByIssueCode = createAsyncThunk<any, any, { state: any }>(
  'bom/removeRowsByIssueCode',
  async ({ issueCode }: { issueCode: IssueCode }, thunkAPI) => {
    try {
      const currentState = thunkAPI.getState()

      const bomItemsId = currentState.project.partTypeIds.filter(
        (x) =>
          (currentState.project.flatView[x] as RowDto).issues.findIndex(
            (y) => y.issueCode === issueCode
          ) > -1
      )

      const projectAPI = projectAPIBuilder(thunkAPI.getState)

      const modalTitle = i18n.t('project:delete-parts-count', {
        count: bomItemsId.length,
      })

      thunkAPI.dispatch(
        UIActions.OpenModal('ConfirmationDialog', {
          title: modalTitle,
          onConfirm: () => {
            try {
              return projectAPI.api.DeleteRowOrAssembly(
                projectAPI.projectId,
                bomItemsId
              )
            } catch (err) {
              ShowException(modalTitle, err)
              thunkAPI.dispatch(fetchProject(projectAPI.projectId))
              throw err
            }
          },
        })
      )
    } catch (ex) {
      ShowException('remove rows by issue code', ex)
      throw ex
    }
  }
)

export const RemoveAllWorkingStepsNotAvailableInPortal = createAsyncThunk<
  void,
  null,
  { state: any }
>('bom/removeAllWorkingStepsNotAvailableInPortal', async (_, thunkAPI) => {
  try {
    const currentState = thunkAPI.getState()
    const bomItemsIds = new Set<string>()
    const notAllowedWorkingSteps: WorkingStepDto[] = []

    currentState.project.partTypeIds.forEach((partTypeId) => {
      const bomItem = projectSelectors.bomItemSelector(
        currentState,
        partTypeId
      ) as RowDto

      const workingStepNotAvailableIssue = bomItem.issues?.find(
        (x) => x.issueCode === IssueCode.WorkingStepNotAvailable
      )

      if (workingStepNotAvailableIssue) {
        const notAvailableWorkingStepsTypeInThisRow: WorkingStepType[] =
          JSON.parse(
            workingStepNotAvailableIssue.metadata
          ).NotAllowedWorkingSteps

        if (notAvailableWorkingStepsTypeInThisRow?.length > 0) {
          notAvailableWorkingStepsTypeInThisRow.forEach((wsType) => {
            notAllowedWorkingSteps.push(
              bomItem.workingSteps.find((x) => x.primaryWorkingStep === wsType)
            )
          })
          bomItemsIds.add(partTypeId)
        }
      }
    })

    if (bomItemsIds.size > 0) {
      const modalDescription = i18n.t('project:remove-multiple-working-steps', {
        workingStep: uniqBy(notAllowedWorkingSteps, (x) => x.primaryWorkingStep)
          .map((workingStep) =>
            i18n.t(`common:${workingStep.primaryWorkingStep}`)
          )
          .join(' / '),
        count: bomItemsIds.size,
        defaultValue:
          'remove working steps ({{workingStep}}) from project? ({{count}} parts affected)',
      })

      thunkAPI.dispatch(
        UIActions.OpenModal('ConfirmationDialog', {
          message: modalDescription,
          title: i18n.t(
            'project:delete-working-steps-title',
            'delete working steps'
          ),
          onConfirm: async () => {
            try {
              const controller = new WorkingStepsController()

              return await controller.handleSaveAndRemoveWorkingSteps(
                Array.from(bomItemsIds),
                [],
                uniqBy(notAllowedWorkingSteps, (x) => x.primaryWorkingStep)
              )
            } catch (err) {
              ShowException(modalDescription, err)
              thunkAPI.dispatch(
                fetchProject({
                  projectId: currentState.project.activeProject?.id,
                })
              )
              throw err
            }
          },
        })
      )
    }
  } catch (ex) {
    throw ex
  }
})
